import React, { useEffect } from "react";
import { useTheme } from "react-jss";
import classnames from "classnames";
import { kebabCase } from "../../../utils/lodash";
import { RendererProps } from "../BaseRenderer";
import { QuestionType, isGrid } from "../../../models/enums/QuestionType";
import { ColumnType } from "../../../models/enums/ColumnType";
import ValidationError from "../../shared/ValidationError";
import { ValidationResultModel } from "../../../models/ValidationResultModel";
import { isNullOrEmpty } from "../../../utils";
import useStyles from "./QuestionRenderer.styles";
import { translate } from "../../../models/utilities/translationUtilities";
import useDimensionContext from "../../../hooks/useDimensionContext";
import ScrollableContainer from "../../shared/ScrollableContainer";
import { DebugInfo } from "../../debug/DebugInfo";
import useRecall from "../../../hooks/useRecall";
import { RecalledText } from "../../../containers/shared/RecalledText";
import { useElementDimensions } from "../../../hooks/useElementDimensions";
import { StoreState } from "../../../models/states/StoreState";
import { useDispatch, useSelector } from "react-redux";
import { isFieldsetGroupingQuestion } from "../../../utils/WCAGHelper";
import { QuestionModel } from "../../../models/QuestionModel";
import { setDefaultFocusStatus } from "../../../redux/actions";
import type { Theme } from "../../../types/Theme";

interface Props extends RendererProps {
    isHidden?: boolean;
    control: any;
    language: string;
    validationResult?: ValidationResultModel;
    isDesktopPreviewView: boolean;
}

const getTitle = ({ question, language }) => translate(question, "TITLE", language);
const getText = ({ question, language }) => translate(question, "TEXT", language);
const getShortText = ({ question, language }) => translate(question, "SHORT_TEXT", language);

function typeClass({ question }) {
    const classes = QuestionType[question.type];
    return `question--type-${kebabCase(classes)}`;
}

function ErrorMessageContainer({ question, questionnaire, language, validationResult }) {
    const theme = useTheme();
    const classes = useStyles({ theme });
    const firstErrorVariable = Object.keys(validationResult.errorsByVariableName)[0];
    const variableErrors = validationResult.errorsByVariableName[firstErrorVariable];
    if (variableErrors) {
        const firstError = variableErrors[0];
        if (firstError) {
            return (
                <div className={classes.questionValidation}>
                    <ValidationError
                        question={question}
                        key={firstError}
                        questionnaire={questionnaire}
                        language={language}
                        error={firstError}
                    />
                </div>
            );
        }
    }
    return <div className={classes.questionValidation} />;
}

function QuestionTitle({ question, title, classes }) {
    const recalledTitle = useRecall(title);

    return (
        <h2
            id={`${question.elementId}_TITLE`}
            className={classes.questionTitle}
            dangerouslySetInnerHTML={{ __html: recalledTitle }}
        />
    );
}

export function RecalledQuestionLabel({ text }) {
    const {
        ref,
        dimensions: { innerHeight },
    } = useElementDimensions<HTMLDivElement>();
    const [minHeight, setMinHeight] = React.useState(0);

    React.useLayoutEffect(() => {
        if (innerHeight > minHeight) {
            setMinHeight(innerHeight);
        }
    }, [innerHeight, minHeight]);

    // RecalledText gets removed and added back from dom on iOS and that causes critical layout issues
    // Setting min height fixes this issue
    return (
        <div ref={ref} style={{ minHeight }}>
            <RecalledText text={text} />
        </div>
    );
}

interface QuestionLabelProps {
    question: QuestionModel;
    text: any;
    shortText: any;
    classes: any;
    elementNameOrId?: string | null;
}

export function QuestionLabel({
    question,
    text,
    shortText,
    classes,
    elementNameOrId,
}: QuestionLabelProps) {
    const device = useSelector((state: StoreState) => state.interview.device);
    const isIOS = ["ios", "mac_os_x", "mac_os"].includes(device?.os?.toLowerCase());
    return (
        <>
            <QuestionLabelContainer
                elementNameOrId={elementNameOrId}
                question={question}
                classes={classes}
            >
                <QuestionText text={text} isIOS={isIOS} />
                <QuestionShortText question={question} shortText={shortText} classes={classes} />
            </QuestionLabelContainer>
        </>
    );
}

const QuestionLabelContainer = ({
    elementNameOrId,
    question,
    children,
    classes,
}: {
    elementNameOrId?: string;
    question: any;
    children: React.ReactNode;
    classes: any;
}) => {
    return (
        <div id={`${elementNameOrId ?? question.elementId}_LABEL`} className={classes.questionText}>
            {children}
        </div>
    );
};

const QuestionText = ({ text, isIOS }: { text: string; isIOS: boolean }) =>
    isIOS ? <RecalledQuestionLabel text={text} /> : <RecalledText text={text} />;

const QuestionShortText = ({
    shortText,
    question,
    classes,
}: {
    shortText: string;
    question: QuestionModel;
    classes: any;
}) => {
    if (isGrid(question.type) || isNullOrEmpty(shortText)) {
        return null;
    }

    return (
        <>
            <RecalledText
                text={shortText}
                render={(recalled) => (
                    <p
                        className={classes.questionShortText}
                        dangerouslySetInnerHTML={{ __html: recalled }}
                    />
                )}
            />
        </>
    );
};

function QuestionContent({ isDesktopPreviewView, question, control, classes }) {
    let content = (
        <>
            {control}
            <div className="clear" />
        </>
    );
    if (isGrid(question.type)) {
        content = <ScrollableContainer>{content}</ScrollableContainer>;
    }
    return <div className={classes.questionContent}>{content}</div>;
}

export default function QuestionRenderer(props: Props) {
    const theme = useTheme() as Theme;
    const classes = useStyles({ theme });
    const dimension = useDimensionContext();
    const isDisplayWide = dimension && dimension.minWidth(778);

    const title = getTitle(props);
    const text = getText(props);
    const shortText = getShortText(props);

    const {
        questionnaire,
        question,
        language,
        isHidden,
        control,
        validationResult,
        isDesktopPreviewView,
    } = props;

    const isValid = !validationResult || validationResult.isValid;
    const hasTitle = !isNullOrEmpty(title);
    const useFieldsetGrouping = isFieldsetGroupingQuestion(question);

    const questionContent = (
        <QuestionContent
            isDesktopPreviewView={isDesktopPreviewView}
            question={question}
            control={control}
            classes={classes}
        />
    );
    if (question.type == QuestionType.LookupTable) {
        const defaultFocusStatus = useSelector((state: StoreState) => state.defaultFocusStatus);
        const dispatch = useDispatch();
        useEffect(() => {
            const elementId = `${question.id}_LOOKUP_TABLE`;
            const existingElement = defaultFocusStatus.statuses.find(
                (element) => element.elementId === elementId,
            );
            if (!existingElement) {
                defaultFocusStatus.statuses.push({
                    elementId,
                    elementRef: null,
                    variableId: 0,
                    questionId: question.id,
                    focusStatus: false,
                });
            }
            dispatch(setDefaultFocusStatus(defaultFocusStatus));
        });
    }

    const titleComponent = hasTitle ? (
        <QuestionTitle question={question} title={title} classes={classes} />
    ) : null;

    const hasTextContent = !isNullOrEmpty(text) || !isNullOrEmpty(shortText);
    const labelComponent =
        !hasTitle || hasTextContent ? (
            <QuestionLabel
                question={question}
                text={text}
                shortText={shortText}
                classes={classes}
                elementNameOrId={null}
            />
        ) : null;

    let headerContent;
    if (useFieldsetGrouping) {
        // Body text/short text must be inside the legend too (DEV-98354/98356) — a fieldset's
        // accessible name comes from its legend only, so content outside it is never announced.
        headerContent = (
            <legend className={classes.questionLegend}>
                {titleComponent}
                {labelComponent}
            </legend>
        );
    } else {
        headerContent = (
            <div className={classes.questionLegend}>
                {titleComponent}
                {labelComponent}
            </div>
        );
    }

    const isChoiceGridCheckbox =
        [QuestionType.ChoiceGrid, QuestionType.ImageChoiceGrid].includes(question.type) &&
        Boolean(question.columns?.some((column) => column.type === ColumnType.CheckBox));

    const liveRegionProps =
        question.type === QuestionType.DropDownList ||
        question.type === QuestionType.RespondentLanguage ||
        question.type === QuestionType.RespondentTimeZone ||
        question.type === QuestionType.AutoComplete ||
        question.type === QuestionType.NumericRanking ||
        question.type === QuestionType.StarRatingGrid ||
        question.type === QuestionType.FileUpload ||
        question.type === QuestionType.HybridGrid ||
        question.type === QuestionType.Carrousel ||
        question.type === QuestionType.ChoiceGrid ||
        question.type === QuestionType.ImageChoiceGrid ||
        // DEV-98353/98354/98356: these now carry a full Title+Label name via aria-labelledby on
        // the focusable control or a named group wrapper (see WCAGHelper.ts); the section live
        // region would otherwise double-announce them, matching the pattern already used above.
        question.type === QuestionType.TextAnswer ||
        question.type === QuestionType.EmailAddressAnswer ||
        question.type === QuestionType.NumericAnswer ||
        question.type === QuestionType.OpenEndAnswer ||
        question.type === QuestionType.RespondentEmail ||
        question.type === QuestionType.RespondentPhone
            ? {}
            : isChoiceGridCheckbox
              ? {}
              : { "aria-live": "polite" as const, "aria-atomic": true as const };

    return (
        <section
            id={question.elementId}
            {...liveRegionProps}
            className={classnames(
                classes.question,
                typeClass(props),
                !isValid && classes.questionError,
            )}
            style={{ display: isHidden ? "none" : undefined }}
        >
            <DebugInfo>{question.name}</DebugInfo>
            {!isValid && (
                <ErrorMessageContainer
                    question={question}
                    questionnaire={questionnaire}
                    language={language}
                    validationResult={validationResult}
                />
            )}
            <div
                className={classnames(
                    !isValid &&
                        !theme.UseModernTheme &&
                        classes.questionValidationFocus,
                    !isValid &&
                        !theme.UseModernTheme &&
                        isDisplayWide &&
                        classes.dynamicQuestionValidationFocus,
                )}
            >
                {useFieldsetGrouping ? (
                    <fieldset className={classes.fieldset}>
                        {headerContent}
                        {questionContent}
                    </fieldset>
                ) : (
                    <div
                        className="non-fieldset-grouping-question"
                        aria-invalid={!isValid}
                        aria-describedby={
                            isValid || isGrid(question.type)
                                ? undefined
                                : `${question.elementId}_ERROR`
                        }
                    >
                        {headerContent}
                        {questionContent}
                    </div>
                )}
            </div>
        </section>
    );
}
